// Матчмейкинг и уведомления — клей между клиентом присутствия (presence.ts), ящиком (inbox.ts), лентой
// (notify.ts) и экранами: подключение при старте, «чем занят этот клиент» из состояния, приглашения
// (в ящик, в ленту, системным уведомлением, если вкладка не на виду), вход в комнату из списка или
// приглашения, зов игрока из лобби, окна ящика и «что нового».
import type { InviteMessage } from '../types.ts';
import { PresenceClient } from './presence.ts';
import { clientId, deviceKey } from './outbox.ts';
import { notify } from './notify.ts';
import { esc } from './cards.ts';
import * as OV from './overlays.ts';
import { st, ui, toast, openModal, closeModal, save } from './store.ts';
import { repaintSocial } from './render.ts';
import { startOnline } from './flow.ts';
import { pushItem, inviteItem, checkChangelog, inboxItems, markAllRead, markRead, newsEnabled, sysPermission, sysNotify, onInboxChange } from './inbox.ts';

const NAME_KEY = 'krutagidon-name';

/** Первый запуск на устройстве — новостями не встречаем. Считается при загрузке модуля: clientId создаётся
 *  первой же отрисовкой стартового экрана (пометка «(ты)» в списке онлайн), позже проверять было бы поздно */
const FIRST_RUN = ((): boolean => {
  try {
    return !localStorage.getItem('krutagidon-client-id');
  } catch {
    return true;
  }
})();

export const myClientId = (): string => clientId();

/** Подключение при старте приложения: присутствие, проверка ченджлога, имя из поля ввода */
export function initSocial(): void {
  const client = new PresenceClient(clientId(), deviceKey(), {
    onSnapshot: (s) => {
      st.presence = s;
      repaintSocial();
    },
    onInvite: onInvite,
    onError: (e) => toast('⚠ ' + e, 3000),
    onInvited: () => toast('Приглашение отправлено'),
    onStatus: (connected) => {
      if (!connected) st.presence = null;
      repaintSocial();
    },
  });
  st.pres = client;
  onInboxChange(repaintSocial);
  syncPresence();
  client.start();
  // имя из поля на стартовом экране — сразу в список «кто онлайн» (с задержкой, чтобы не слать каждую букву)
  let nameTimer: ReturnType<typeof setTimeout> | null = null;
  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.id !== 'online-name') return;
    ui.onlineName = t.value.trim();
    rememberName();
    if (nameTimer) clearTimeout(nameTimer);
    nameTimer = setTimeout(() => {
      syncPresence();
      repaintSocial(); // имя в плашке профиля на хабе
    }, 600);
  });
  const news = checkChangelog(FIRST_RUN);
  if (news) notify({ html: `🔔 ${esc(news.title)} — нажми, чтобы посмотреть`, cls: 'n-note', hold: 7000, priority: 'high', sound: 'bell', onTap: openChangelog });
}

export function rememberName(): void {
  try {
    localStorage.setItem(NAME_KEY, ui.onlineName);
  } catch {
    /* ignore */
  }
}

/** Сообщить серверу, чем занят клиент (вызывается из render — состояние экрана и есть источник правды) */
export function syncPresence(): void {
  if (!st.pres) return;
  const mode = st.net ? 'online' : st.G && ui.screen !== 'start' ? 'hotseat' : 'idle';
  st.pres.setState({ name: ui.onlineName, mode, room: st.net?.code ?? '' });
}

/** Приглашение: в ящик, в ленту (тап — открыть ящик), системным уведомлением, если игра не на экране */
function onInvite(m: InviteMessage): void {
  pushItem(inviteItem(m.from, m.code, m.players));
  notify({ html: `🔔 <b>${esc(m.from)}</b> зовёт в комнату ${esc(m.code)} — нажми, чтобы открыть`, cls: 'n-note', hold: 8000, priority: 'high', sound: 'bell', onTap: openInbox });
  if (document.visibilityState !== 'visible') sysNotify('Крутагидон: приглашение', `${m.from} зовёт в комнату ${m.code}`, `invite:${m.code}`);
}

/** Войти в комнату из списка открытых или из приглашения: хотсит-партия сохраняется, к ней можно вернуться */
export function joinRoom(code: string): void {
  closeModal();
  markRead((i) => i.kind === 'invite' && i.code === code);
  if (st.net) {
    if (st.net.code === code) {
      toast('Ты уже в этой комнате');
      return;
    }
    st.net.leave();
    st.net = null;
  } else if (st.G) save();
  if (ui.botTimer) clearTimeout(ui.botTimer);
  ui.botTimer = null;
  st.G = null;
  ui.screen = 'start';
  ui.lastSeq = 0;
  ui.endShown = false;
  ui.joinCode = code;
  startOnline('join');
}

/** Позвать игрока (id клиента) в свою комнату */
export function invitePlayer(id: string): void {
  const code = st.net?.code;
  if (!code) return;
  if (!st.pres?.invite(id, code)) toast('Нет связи с сервером');
}

/** Окно ящика: строится из состояния (ящик, настройки), так что после «убрать»/переключения хватает repaintModal */
export function openInbox(): void {
  openModal(() => OV.inboxV(inboxItems(), { news: newsEnabled(), sys: sysPermission() }));
  markAllRead();
}
export function openChangelog(): void {
  openModal(() => OV.changelogV());
  // новость прочитана — открыли «Что нового» (из ленты, ящика или с хаба): колокольчик не должен её считать
  markRead((i) => i.kind === 'news');
}
